/* eslint-disable react-hooks/exhaustive-deps, no-unused-vars */
import { useEffect, useState, lazy, Suspense, type ReactNode } from 'react'
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom'
import { apiFetch, auth, mapOpportunityDto, mapOrgUserDto, mapProfileDto } from './api'
import { ProfileContext } from './contexts/ProfileContext'
import Auth from './components/Auth'
import AcceptInvite from './components/AcceptInvite'
const Dashboard = lazy(() => import('./components/Dashboard'))
const Qualification = lazy(() => import('./components/Qualification'))
const BidPlan = lazy(() => import('./components/BidPlan'))
const Opportunities = lazy(() => import('./components/Opportunities'))
const Artefacts = lazy(() => import('./components/Artefacts'))
const AtRiskPlans = lazy(() => import('./components/AtRiskPlans'))
const OrgSetup = lazy(() => import('./components/OrgSetup'))
const UserManagement = lazy(() => import('./components/UserManagement'))
const BrandingSettings = lazy(() => import('./components/BrandingSettings'))
const ActionTracker = lazy(() => import('./components/ActionTracker'))
const QuestionTracker = lazy(() => import('./components/QuestionTracker'))
const LicenceSettings = lazy(() => import('./components/LicenceSettings'))
const OrganisationSettings = lazy(() => import('./components/OrganisationSettings'))
const FrameworkLibrary = lazy(() => import('./components/FrameworkLibrary'))
const CompetitorLibrary = lazy(() => import('./components/CompetitorLibrary'))
const TcVarianceAnalysis = lazy(() => import('./components/TcVarianceAnalysis'))
const OpportunityIntelligence = lazy(() => import('./components/OpportunityIntelligence'))
const OpportunityCompetitors = lazy(() => import('./components/OpportunityCompetitors'))
const ReportingSuite = lazy(() => import('./components/ReportingSuite'))
const ClarificationQuestions = lazy(() => import('./components/ClarificationQuestions'))
const MyActions = lazy(() => import('./components/MyActions'))
const AllActions = lazy(() => import('./components/AllActions'))
const AccountSettings = lazy(() => import('./components/AccountSettings'))

type Session = { user: any } | null

type AuthGuardProps = {
  session: Session
  children: ReactNode
}

type OpportunityRouteProps = {
  render: (opportunity: any) => ReactNode
}

const PAGE_TO_PATH = {
  dashboard: '/',
  opportunities: '/opportunities',
  qualification: '/qualification',
  bidplan: '/bid-plans',
  artefacts: '/artefacts',
  atrisk: '/at-risk',
  frameworks: '/frameworks',
  competitive: '/competitive',
  oppintel: '/opportunity-intelligence',
  reporting: '/reporting',
  users: '/users',
  'account-settings': '/account-settings',
  orgsettings: '/settings/organisation',
  branding: '/settings/branding',
  licence: '/settings/licence',
  'my-actions': '/actions/my',
  'all-actions': '/actions/all'
}

function sanitizeReturnTo(value: string | null) {
  if (typeof value !== 'string' || !value.startsWith('/')) {
    return null
  }

  if (value.startsWith('//')) {
    return null
  }

  return value
}

function buildLoginRedirect(location: { pathname: string; search: string }) {
  const returnTo = encodeURIComponent(`${location.pathname}${location.search}`)
  return `/login?returnTo=${returnTo}`
}

function AuthGuard({ session, children }: AuthGuardProps) {
  const location = useLocation()

  if (!session) {
    return <Navigate to={buildLoginRedirect(location)} replace />
  }

  return <>{children}</>
}

function OpportunityRoute({ render }: OpportunityRouteProps) {
  const { oppId } = useParams()
  const location = useLocation() as any
  const stateOpportunity = location.state?.opp
  const hasMatchingState = stateOpportunity && String(stateOpportunity.id) === oppId
  const [opportunity, setOpportunity] = useState<any>(() => (hasMatchingState ? mapOpportunityDto(stateOpportunity) : null))
  const [loading, setLoading] = useState(() => !hasMatchingState)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let active = true
    const nextStateOpportunity = location.state?.opp
    const matchesState = nextStateOpportunity && String(nextStateOpportunity.id) === oppId

    if (matchesState) {
      setOpportunity(mapOpportunityDto(nextStateOpportunity))
      setLoading(false)
      setError(null)
      return () => {
        active = false
      }
    }

    setLoading(true)
    setError(null)

    apiFetch(`/api/opportunities/${oppId}`)
      .then(data => {
        if (!active) {
          return
        }

        setOpportunity(mapOpportunityDto(data || null))
      })
      .catch(() => {
        if (!active) {
          return
        }

        setError('Unable to load opportunity.')
        setOpportunity(null)
      })
      .finally(() => {
        if (active) {
          setLoading(false)
        }
      })

    return () => {
      active = false
    }
  }, [oppId, location.state])

  if (loading) {
    return (
      <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '24px', color: 'var(--slate)' }}>
        Loading opportunity...
      </div>
    )
  }

  if (error || !opportunity) {
    return (
      <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '24px' }}>
        <div className="error-msg">{error || 'Opportunity not found.'}</div>
      </div>
    )
  }

  return <>{render(opportunity)}</>
}

function injectStylesheet(id: string, href: string) {
  let el = document.getElementById(id) as HTMLLinkElement | null
  if (!el) {
    el = document.createElement('link')
    el.id = id
    el.rel = 'stylesheet'
    document.head.appendChild(el)
  }
  if (el.href !== href) el.href = href
}

function removeStylesheet(id: string) {
  document.getElementById(id)?.remove()
}

function injectInlineStyle(id: string, css: string) {
  let el = document.getElementById(id) as HTMLStyleElement | null
  if (!el) {
    el = document.createElement('style')
    el.id = id
    document.head.appendChild(el)
  }
  el.textContent = css
}

function deriveFontFamily(url: string): string {
  const m = url.match(/family=([^:&]+)/)
  return m ? decodeURIComponent(m[1].replace(/\+/g, ' ')) : 'inherit'
}

function isLightColour(hex: string): boolean {
  const r = parseInt(hex.slice(0, 2), 16)
  const g = parseInt(hex.slice(2, 4), 16)
  const b = parseInt(hex.slice(4, 6), 16)
  return (r * 299 + g * 587 + b * 114) / 1000 > 128
}

function AppRouter() {
  const navigate = useNavigate()
  const location = useLocation() as any
  const [session, setSession] = useState<Session>(null)
  const [loading, setLoading] = useState(true)
  const [profile, setProfile] = useState<any>(null)
  const [orgUsers, setOrgUsers] = useState<any[]>([])
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [intelligenceOpen, setIntelligenceOpen] = useState(false)
  const isOrgAdmin = profile?.role === 'org_admin'

  useEffect(() => {
    let active = true

    const initialize = async () => {
      // Handle SSO redirect fragment
      const hash = window.location.hash
      if (hash.startsWith('#sso=success')) {
        const params = new URLSearchParams(hash.slice(1))
        const accessToken = params.get('access_token')
        const refreshToken = params.get('refresh_token')
        if (accessToken) localStorage.setItem('cadence_access_token', accessToken)
        if (refreshToken) localStorage.setItem('cadence_refresh_token', refreshToken)
        if (params.get('impersonated') === 'true') {
          sessionStorage.setItem('cadence_impersonated', 'true')
        }
        window.location.hash = ''
      }

      const user = auth.getUser()
      if (!active) {
        return
      }

      if (user) {
        setSession({ user })
        await loadProfile(user.id)
      }

      if (active) {
        setLoading(false)
      }
    }

    const handleLogout = () => {
      if (!active) {
        return
      }

      setSession(null)
      setProfile(null)
      setOrgUsers([])
      setLoading(false)
      navigate('/login', { replace: true })
    }

    initialize()
    window.addEventListener('auth:logout', handleLogout)

    return () => {
      active = false
      window.removeEventListener('auth:logout', handleLogout)
    }
  }, [])

  const loadProfile = async (userId: any) => {
    try {
      const currentUser = await apiFetch('/api/users/me')
      let organisation = null
      let users: any[] = []

      if (currentUser?.organisationId) {
        organisation = await apiFetch(`/api/organisations/current?t=${Date.now()}`)
        users = await apiFetch('/api/users')
      }

      const nextProfile = mapProfileDto(currentUser, organisation)
      setProfile(nextProfile)
      setOrgUsers((users || []).map(mapOrgUserDto))
    } catch {
      if (session?.user?.id === userId) {
        await auth.logout()
      }
    }
  }

  const refreshProfile = async () => {
    if (session?.user?.id) {
      await loadProfile(session.user.id)
    }
  }

  useEffect(() => {
    const org = profile?.organisations
    if (!org) return
    const root = document.documentElement
    const hex = (val: string | null | undefined, fallback: string) =>
      val ? `#${val}` : fallback
    root.style.setProperty('--color-primary',      hex(org.primary_colour,   '#2D6A4F'))
    root.style.setProperty('--color-primary-dark', hex(org.header_colour ?? org.primary_colour, '#1A4035'))
    root.style.setProperty('--color-button',       hex(org.button_colour ?? org.primary_colour, '#2D6A4F'))
    root.style.setProperty('--color-nav-bg',       hex(org.header_colour ?? org.primary_colour, '#1A4035'))
    root.style.setProperty('--color-nav-border',   hex(org.primary_colour,   '#2D6A4F'))
    root.style.setProperty('--color-secondary',    hex(org.secondary_colour, '#C9A84C'))
    root.style.setProperty('--color-accent',       hex(org.accent_colour ?? org.secondary_colour, '#C9A84C'))
    if (org.page_bg_colour) {
      root.style.setProperty('--color-page-bg',   `#${org.page_bg_colour}`)
      if (!org.page_text_colour) {
        root.style.setProperty('--color-page-text', isLightColour(org.page_bg_colour) ? '#1C1C1C' : '#F0F0F0')
      }
    } else {
      root.style.removeProperty('--color-page-bg')
      root.style.removeProperty('--color-page-text')
    }
    if (org.page_text_colour) {
      root.style.setProperty('--color-page-text', `#${org.page_text_colour}`)
    }
    if (org.heading_font_url) {
      injectStylesheet('org-heading-font', org.heading_font_url)
      root.style.setProperty('--font-heading', `'${deriveFontFamily(org.heading_font_url)}', Georgia, serif`)
    }
    if (org.body_font_url) {
      injectStylesheet('org-body-font', org.body_font_url)
      root.style.setProperty('--font-body', `'${deriveFontFamily(org.body_font_url)}', system-ui, sans-serif`)
    }
    injectInlineStyle('org-css-overrides', org.custom_css_overrides ?? '')
    if (org.custom_css_url) {
      injectStylesheet('org-custom-css', org.custom_css_url)
    } else {
      removeStylesheet('org-custom-css')
    }
  }, [
    profile?.organisations?.id,
    profile?.organisations?.primary_colour,
    profile?.organisations?.secondary_colour,
    profile?.organisations?.button_colour,
    profile?.organisations?.header_colour,
    profile?.organisations?.accent_colour,
    profile?.organisations?.custom_css_url,
    profile?.organisations?.custom_css_overrides,
    profile?.organisations?.heading_font_url,
    profile?.organisations?.body_font_url,
    profile?.organisations?.page_bg_colour,
    profile?.organisations?.page_text_colour,
  ])

  const navigateTo = (page: any, context?: any) => {
    switch (page) {
      case 'opportunities': {
        const filter = typeof context === 'string' && context ? `?filter=${encodeURIComponent(context)}` : ''
        navigate(`/opportunities${filter}`)
        return
      }
      case 'frameworks': {
        const id = typeof context === 'string' ? context : context?.id
        navigate(id ? `/frameworks?id=${encodeURIComponent(id)}` : '/frameworks')
        return
      }
      case 'competitive': {
        const id = typeof context === 'string' ? context : context?.id
        navigate(id ? `/competitive?id=${encodeURIComponent(id)}` : '/competitive')
        return
      }
      case 'atrisk': {
        const opps = Array.isArray(context) ? context : context?.opps
        navigate('/at-risk', { state: { opps: opps || [] } })
        return
      }
      default: {
        navigate(PAGE_TO_PATH[page as keyof typeof PAGE_TO_PATH] || '/')
      }
    }
  }

  const handleOpenBidPlan = (opp: any, planId?: any) => {
    navigate(`/opportunities/${opp.id}/bid-plan`, {
      state: { opp: planId ? { ...opp, planId } : opp }
    })
  }

  const handleOpenAtRiskPlan = (opps: any[]) => {
    navigate('/at-risk', { state: { opps } })
  }

  const handleOpenArtefacts = (opp: any) => {
    navigate(`/opportunities/${opp.id}/artefacts`, { state: { opp } })
  }

  const handleOpenActions = (opp: any) => {
    navigate(`/opportunities/${opp.id}/actions`, { state: { opp } })
  }

  const handleOpenQuestions = (opp: any) => {
    navigate(`/opportunities/${opp.id}/questions`, { state: { opp } })
  }

  const handleOpenTcAnalysis = (opp: any) => {
    navigate(`/opportunities/${opp.id}/tc-analysis`, { state: { opp } })
  }

  const handleOpenCompetitors = (opp: any) => {
    navigate(`/opportunities/${opp.id}/competitors`, { state: { opp } })
  }

  const handleOpenFramework = (id: any) => {
    navigateTo('frameworks', id)
  }

  const handleOpenCqs = (opp: any) => {
    navigate(`/opportunities/${opp.id}/cqs`, { state: { opp } })
  }

  const handleAuthenticated = async (user: any) => {
    setSession({ user })
    setLoading(true)
    await loadProfile(user.id)
    setLoading(false)
  }

  const handleSignOut = async () => {
    await auth.logout()
  }

  const isActivePage = (page: string) => {
    switch (page) {
      case 'dashboard':
        return location.pathname === '/'
      case 'opportunities':
        return location.pathname === '/opportunities'
      case 'qualification':
        return location.pathname === '/qualification'
      case 'bidplan':
        return location.pathname === '/bid-plans' || location.pathname.endsWith('/bid-plan')
      case 'artefacts':
        return location.pathname === '/artefacts' || location.pathname.endsWith('/artefacts')
      case 'frameworks':
        return location.pathname === '/frameworks'
      case 'competitive':
        return location.pathname === '/competitive'
      case 'oppintel':
        return location.pathname === '/opportunity-intelligence'
      case 'reporting':
        return location.pathname === '/reporting'
      default:
        return false
    }
  }

  if (loading) {
    return null
  }

  const loginReturnTo = sanitizeReturnTo(new URLSearchParams(location.search).get('returnTo'))
  const profileContextValue = { session, profile, orgUsers, refreshProfile }

  const protectedApp = !session
    ? null
    : session && profile !== null && !profile?.organisation_id
      ? <OrgSetup session={session} onComplete={() => loadProfile(session.user.id)} />
      : (
      <div onClick={() => { setSettingsOpen(false); setIntelligenceOpen(false) }}>
        <nav className="app-nav">
          <a className="app-nav__brand" href="/">
            {profile?.organisations?.logo_url ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <img
                  src={profile.organisations.logo_url}
                  alt={profile.organisations.name}
                  style={{ height: '34px', maxWidth: '160px', objectFit: 'contain' }}
                />
                {profile.organisations.powered_by_cadence && (
                  <span style={{ fontSize: '0.5rem', opacity: 0.5, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--white)', marginTop: '2px' }}>
                    Powered by Cadence
                  </span>
                )}
              </div>
            ) : (
              <>
                CADENCE <span style={{ fontSize: '0.6875rem', opacity: 0.6, letterSpacing: '0.08em' }}>by Captus</span>
              </>
            )}
          </a>
          <div className="app-nav__links">
            {!isOrgAdmin && <a className={`app-nav__link ${isActivePage('dashboard') ? 'active' : ''}`} href="/">Dashboard</a>}
            {!isOrgAdmin && <a className={`app-nav__link ${isActivePage('opportunities') ? 'active' : ''}`} href="/opportunities">Pipeline</a>}
            {!isOrgAdmin && <a className={`app-nav__link ${isActivePage('qualification') ? 'active' : ''}`} href="/qualification">Qualify</a>}
            {!isOrgAdmin && <a className={`app-nav__link ${isActivePage('bidplan') ? 'active' : ''}`} href="/bid-plans">Bid Plan</a>}
            {!isOrgAdmin && <a className={`app-nav__link ${isActivePage('artefacts') ? 'active' : ''}`} href="/artefacts">Artefacts</a>}
            {!isOrgAdmin && <a className={`app-nav__link ${isActivePage('frameworks') ? 'active' : ''}`} href="/frameworks">Frameworks</a>}
            {!isOrgAdmin && (
              <div style={{ position: 'relative' }}>
                <button
                  className={`app-nav__link ${isActivePage('reporting') || isActivePage('competitive') || isActivePage('oppintel') ? 'active' : ''}`}
                  onClick={e => { e.stopPropagation(); setIntelligenceOpen(prev => !prev) }}
                >
                  Intelligence {intelligenceOpen ? '▲' : '▼'}
                </button>
                {intelligenceOpen && (
                  <div
                    onClick={e => e.stopPropagation()}
                    style={{ position: 'absolute', top: '100%', left: 0, background: 'var(--color-card-bg)', border: '1px solid var(--color-card-border)', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', minWidth: '220px', zIndex: 1000, marginTop: '4px' }}
                  >
                    <a
                      href="/reporting"
                      onClick={() => { setIntelligenceOpen(false) }}
                      style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 16px', background: 'none', border: 'none', borderBottom: '1px solid var(--color-card-border)', fontSize: '0.875rem', color: 'var(--color-page-text)', cursor: 'pointer', fontFamily: 'inherit', fontWeight: isActivePage('reporting') ? 700 : 400, textDecoration: 'none' }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'var(--color-primary-pale)'; e.currentTarget.style.color = 'var(--charcoal)' }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = 'var(--color-page-text)' }}
                    >
                      Reporting and Analytics
                    </a>
                    {([
                      { label: 'Competitive Intelligence', page: 'competitive', active: true },
                      { label: 'Opportunity Intelligence', page: 'oppintel', active: true },
                      { label: 'Framework Intelligence', page: 'frameworks', active: true },
                      { label: 'T&C Variance Analysis', page: 'opportunities', active: true }
                    ] as Array<{ label: string; page: string; active: boolean; note?: string }>).map(item => (
                      item.active ? (
                        <a
                          key={item.label}
                          href={PAGE_TO_PATH[item.page as keyof typeof PAGE_TO_PATH] || '/'}
                          onClick={() => { setIntelligenceOpen(false) }}
                          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', borderBottom: '1px solid var(--color-card-border)', opacity: 1, cursor: 'pointer', background: 'none', textDecoration: 'none', color: 'var(--color-page-text)' }}
                          onMouseEnter={e => { e.currentTarget.style.background = 'var(--color-primary-pale)'; e.currentTarget.style.color = 'var(--charcoal)' }}
                          onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = 'var(--color-page-text)' }}
                        >
                          <span style={{ fontSize: '0.875rem', fontFamily: 'inherit' }}>{item.label}</span>
                        </a>
                      ) : (
                        <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', borderBottom: '1px solid var(--color-card-border)', opacity: 0.5, cursor: 'default', background: 'none' }}>
                          <span style={{ fontSize: '0.875rem', color: 'var(--slate)', fontFamily: 'inherit' }}>{item.label}</span>
                          <span style={{ fontSize: '0.625rem', fontWeight: 600, color: 'var(--slate)', letterSpacing: '0.06em' }}>{item.note}</span>
                        </div>
                      )
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="app-nav__user">
            <span>{profile?.username || session?.user?.email}</span>
            <div style={{ position: 'relative' }}>
              <button className="app-nav__signout" onClick={(e) => { e.stopPropagation(); setSettingsOpen(prev => !prev) }}>
                ⚙ Settings
              </button>
              {settingsOpen && (
                <div onClick={e => e.stopPropagation()} style={{ position: 'absolute', top: '100%', right: 0, background: 'var(--color-card-bg)', border: '1px solid var(--color-card-border)', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', minWidth: '220px', zIndex: 1000, marginTop: '4px' }}>
                    <a href="/account-settings" onClick={() => { setSettingsOpen(false) }} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 16px', background: 'none', border: 'none', fontSize: '0.875rem', color: 'var(--color-page-text)', cursor: 'pointer', fontFamily: 'inherit', textDecoration: 'none' }} onMouseEnter={e => { e.currentTarget.style.background = 'var(--color-primary-pale)'; e.currentTarget.style.color = 'var(--charcoal)' }} onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = 'var(--color-page-text)' }}>
                    👤 Account Settings
                  </a>
                  {(profile?.role === 'admin' || profile?.role === 'org_admin') && (
                    <a href="/settings/organisation" onClick={() => { setSettingsOpen(false) }} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 16px', background: 'none', border: 'none', borderTop: '1px solid var(--color-card-border)', fontSize: '0.875rem', color: 'var(--color-page-text)', cursor: 'pointer', fontFamily: 'inherit', textDecoration: 'none' }} onMouseEnter={e => { e.currentTarget.style.background = 'var(--color-primary-pale)'; e.currentTarget.style.color = 'var(--charcoal)' }} onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = 'var(--color-page-text)' }}>
                      ⚙ Organisation Settings
                    </a>
                  )}
                </div>
              )}
            </div>
            <button className="app-nav__signout" onClick={handleSignOut}>Sign Out</button>
          </div>
        </nav>
        {sessionStorage.getItem('cadence_impersonated') === 'true' && (
          <div style={{ background: '#fff3cd', color: '#856404', padding: '8px 24px', textAlign: 'center', fontWeight: 600, fontSize: '0.875rem' }}>
            Impersonation active. Actions are logged to the target user's audit trail.
            <button onClick={handleSignOut} style={{ marginLeft: '16px', background: '#856404', color: '#fff', border: 'none', padding: '4px 12px', borderRadius: '4px', cursor: 'pointer', fontWeight: 600 }}>
              End Impersonation
            </button>
          </div>
        )}
        <div className="app-content">
          <Suspense fallback={<div className="page-spinner"><div className="page-spinner__ring" /></div>}>
          <Routes>
            {!isOrgAdmin && (<>
            <Route path="/" element={
              <Dashboard
                key="dashboard"
                onNavigate={navigateTo}
                onOpenAtRiskPlan={handleOpenAtRiskPlan}
              />
            }
            />
            <Route path="/opportunities" element={
              <Opportunities
                key={location.search || 'default'}
                initialFilter={new URLSearchParams(location.search).get('filter') || undefined}
                onNavigate={navigateTo}
                onOpenBidPlan={handleOpenBidPlan}
                onOpenArtefacts={handleOpenArtefacts}
                onOpenActions={handleOpenActions}
                onOpenQuestions={handleOpenQuestions}
                onOpenCqs={handleOpenCqs}
                onOpenFramework={handleOpenFramework}
                onOpenCompetitors={handleOpenCompetitors}
                onOpenTcAnalysis={handleOpenTcAnalysis}
              />
            }
            />
            <Route path="/qualification" element={
              <Qualification
                onNavigate={navigateTo}
                preloadedOpportunity={null}
                existingScore={null}
                onScoreSaved={undefined}
              />
            }
            />
            <Route path="/bid-plans" element={
              <BidPlan
                onNavigate={navigateTo}
                linkedOpportunity={null}
                onOpenArtefacts={handleOpenArtefacts}
                onOpenActions={handleOpenActions}
                onOpenQuestions={handleOpenQuestions}
                onOpenCqs={handleOpenCqs}
              />
            }
            />
            <Route path="/opportunities/:oppId/bid-plan" element={
              <OpportunityRoute
                render={opportunity => (
                  <BidPlan
                    onNavigate={navigateTo}
                    linkedOpportunity={opportunity}
                    onOpenArtefacts={handleOpenArtefacts}
                    onOpenActions={handleOpenActions}
                    onOpenQuestions={handleOpenQuestions}
                    onOpenCqs={handleOpenCqs}
                  />
                )}
              />
            }
            />
            <Route path="/at-risk" element={
              <AtRiskPlans
                onNavigate={navigateTo}
                atRiskOpps={location.state?.opps || []}
                onOpenBidPlan={handleOpenBidPlan}
              />
            }
            />
            <Route path="/opportunities/:oppId/questions" element={
              <OpportunityRoute
                render={opportunity => (
                  <QuestionTracker
                    opportunity={opportunity}
                    onClose={() => navigate('/opportunities')}
                  />
                )}
              />
            }
            />
            <Route path="/opportunities/:oppId/cqs" element={
              <OpportunityRoute
                render={opportunity => (
                  <ClarificationQuestions
                    opportunity={opportunity}
                    onClose={() => navigate('/opportunities')}
                  />
                )}
              />
            }
            />
            <Route path="/opportunities/:oppId/actions" element={
              <OpportunityRoute
                render={opportunity => (
                  <ActionTracker
                    opportunity={opportunity}
                    onClose={() => navigate('/opportunities')}
                  />
                )}
              />
            }
            />
            <Route path="/actions/my" element={
              <MyActions
                onNavigate={navigateTo}
              />
            }
            />
            <Route path="/actions/all" element={
              <AllActions
                onNavigate={navigateTo}
              />
            }
            />
            <Route path="/artefacts" element={
              <Artefacts
                onNavigate={navigateTo}
                linkedOpportunity={null}
              />
            }
            />
            <Route path="/opportunities/:oppId/artefacts" element={
              <OpportunityRoute
                render={opportunity => (
                  <Artefacts
                    onNavigate={navigateTo}
                    linkedOpportunity={opportunity}
                  />
                )}
              />
            }
            />
            </>)}
            {isOrgAdmin && (
              <Route path="/" element={
                <div className="dashboard">
                  <div className="card" style={{ maxWidth: '480px', margin: '2rem auto', padding: '1.5rem' }}>
                    <h1 style={{ fontSize: '1.25rem', margin: '0 0 0.25rem' }}>{profile?.organisations?.name}</h1>
                    <p style={{ fontSize: '0.875rem', color: 'var(--color-page-text)', margin: '0 0 1.25rem' }}>
                      Organisation Overview
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid var(--color-card-border)' }}>
                        <span style={{ fontWeight: 500 }}>Users</span>
                        <span>{orgUsers.length}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid var(--color-card-border)' }}>
                        <span style={{ fontWeight: 500 }}>Licence Tier</span>
                        <span style={{ textTransform: 'capitalize' }}>{profile?.organisations?.licence_tier ?? '—'}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid var(--color-card-border)' }}>
                        <span style={{ fontWeight: 500 }}>Licence Status</span>
                        <span style={{ textTransform: 'capitalize' }}>{profile?.organisations?.licence_status ?? '—'}</span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem' }}>
                      <a href="/users" style={{ flex: 1, textAlign: 'center', padding: '0.5rem 1rem', background: 'var(--color-button)', color: '#fff', borderRadius: '6px', textDecoration: 'none', fontSize: '0.875rem' }}>
                        User Management
                      </a>
                      <a href="/settings/organisation" style={{ flex: 1, textAlign: 'center', padding: '0.5rem 1rem', background: 'var(--color-button)', color: '#fff', borderRadius: '6px', textDecoration: 'none', fontSize: '0.875rem' }}>
                        Org Settings
                      </a>
                    </div>
                  </div>
                </div>
              } />
            )}
            <Route path="/users" element={
              <UserManagement
                onNavigate={navigateTo}
              />
            }
            />
            {!isOrgAdmin && (<>
            <Route path="/opportunities/:oppId/tc-analysis" element={
              <OpportunityRoute
                render={opportunity => (
                  <TcVarianceAnalysis
                    opportunity={opportunity}
                    onClose={() => navigate('/opportunities')}
                  />
                )}
              />
            }
            />
            <Route path="/competitive" element={
              <CompetitorLibrary
                key={location.search || 'default'}
                onNavigate={navigateTo}
                initialCompetitorId={new URLSearchParams(location.search).get('id') || undefined}
              />
            }
            />
            <Route path="/opportunities/:oppId/competitors" element={
              <OpportunityRoute
                render={opportunity => (
                  <OpportunityCompetitors
                    opportunity={opportunity}
                    onClose={() => navigate('/opportunities')}
                  />
                )}
              />
            }
            />
            <Route path="/opportunity-intelligence" element={
              <OpportunityIntelligence
                onNavigate={navigateTo}
              />
            }
            />
            <Route path="/reporting" element={
              <ReportingSuite
                onNavigate={navigateTo}
              />
            }
            />
            <Route path="/frameworks" element={
              <FrameworkLibrary
                key={location.search || 'default'}
                onNavigate={navigateTo}
                initialFrameworkId={new URLSearchParams(location.search).get('id') || undefined}
                onOpenCompetitor={(id) => navigateTo('competitive', id)}
              />
            }
            />
            </>)}
            <Route path="/settings/organisation" element={
              <OrganisationSettings
                onNavigate={navigateTo}
                initialTab={undefined}
                onSettingsSaved={refreshProfile}
              />
            }
            />
            <Route path="/settings/licence" element={
              <LicenceSettings
                onNavigate={navigateTo}
              />
            }
            />
            <Route path="/settings/branding" element={
              <BrandingSettings
                onNavigate={navigateTo}
                onSettingsSaved={refreshProfile}
                onBrandingUpdated={refreshProfile}
              />
            }
            />
            <Route path="/account-settings" element={
              <AccountSettings
                onNavigate={navigateTo}
              />
            }
            />
            <Route path="*" element={<Navigate to={isOrgAdmin ? "/settings/organisation" : "/"} replace />} />
          </Routes>
          </Suspense>
        </div>
      </div>
        )

  return (
    <Routes>
      <Route
        path="/login"
        element={session ? <Navigate to={loginReturnTo || '/'} replace /> : <Auth onAuthenticated={handleAuthenticated} />}
      />
      <Route
        path="/accept-invite"
        element={<AcceptInvite />}
      />
      <Route
        path="/*"
        element={
          <AuthGuard session={session}>
            <ProfileContext.Provider value={profileContextValue}>
              {protectedApp}
            </ProfileContext.Provider>
          </AuthGuard>
        }
      />
    </Routes>
  )
}

export default AppRouter
